/**
 * Timezone helpers for the operations team's frame of reference (APP-1720).
 *
 * The study team sits in Eastern, so the appointment list is read and filtered in
 * Eastern regardless of where a coordinator's browser happens to be. Before this,
 * the date pickers produced a `Date` in the *browser's* zone while the appointment
 * column rendered in the *patient's* zone, so filtering for what was on screen
 * returned nothing: a 9:00 AM PDT booking is 13:00Z, but an Eastern browser asking
 * for "9–10 AM" sends 13:00Z–14:00Z... for its own 9 AM, which is 13:00Z only by
 * coincidence of the date. The two frames never lined up.
 *
 * Everything here goes through `Intl` with an IANA zone id rather than a fixed
 * offset, so daylight-saving transitions are handled by the runtime's tz database
 * instead of by arithmetic that is wrong for half the year.
 */

/** Where the operations team reads appointments. */
export const OPERATIONS_ZONE = 'America/New_York'

/**
 * The offset, in milliseconds, that `zone` was at the given instant.
 *
 * Derived by formatting the instant into the zone's wall-clock fields and reading
 * them back as if they were UTC. The difference is the offset that applied *then*,
 * which is what makes this daylight-saving correct.
 */
const zoneOffsetMs = (instant: Date, zone: string): number => {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: zone,
        hour12: false,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
    }).formatToParts(instant)

    const field = (type: Intl.DateTimeFormatPartTypes): number => Number(parts.find((part) => part.type === type)?.value ?? '0')

    // `hour` comes back as 24 at midnight under hour12: false in some runtimes.
    // Milliseconds are carried over from the instant: the formatter does not emit
    // them, and leaving them at zero would fold the instant's own milliseconds into
    // the offset, putting every computed boundary up to a second out.
    const asIfUtc = Date.UTC(
        field('year'),
        field('month') - 1,
        field('day'),
        field('hour') % 24,
        field('minute'),
        field('second'),
        instant.getUTCMilliseconds()
    )
    return asIfUtc - instant.getTime()
}

/**
 * The instant at which the given wall-clock time occurs in `zone`.
 *
 * Two passes: the first guesses using the offset at the naive instant, the second
 * corrects it using the offset actually in force at that guess. That second pass is
 * what gets the hours either side of a daylight-saving transition right, where the
 * offset before and after the wall-clock time differ.
 */
export const zonedWallClockToUtc = (
    year: number,
    month: number,
    day: number,
    hour: number,
    minute: number,
    second: number,
    ms: number,
    zone: string
): Date => {
    const naive = Date.UTC(year, month - 1, day, hour, minute, second, ms)
    const firstPass = naive - zoneOffsetMs(new Date(naive), zone)
    return new Date(naive - zoneOffsetMs(new Date(firstPass), zone))
}

/** The instant the given calendar day starts in `zone`. */
export const zonedDayStart = (year: number, month: number, day: number, zone: string = OPERATIONS_ZONE): Date =>
    zonedWallClockToUtc(year, month, day, 0, 0, 0, 0, zone)

/** The last instant of the given calendar day in `zone`. */
export const zonedDayEnd = (year: number, month: number, day: number, zone: string = OPERATIONS_ZONE): Date =>
    zonedWallClockToUtc(year, month, day, 23, 59, 59, 999, zone)

/**
 * Read the calendar date a picker produced.
 *
 * A date picker hands back a `Date` at local midnight, so the only meaningful part
 * is the calendar day the coordinator clicked — the clock time is an artefact of
 * their browser. Taking the local Y/M/D keeps the day they chose; reading the
 * instant would shift it for anyone west of the zone.
 */
export const calendarPartsOf = (date: Date): { year: number; month: number; day: number } => ({
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate()
})

/** The day a picker selected, as the instant that day starts in `zone`. */
export const pickedDayStart = (date: Date, zone: string = OPERATIONS_ZONE): Date => {
    const { year, month, day } = calendarPartsOf(date)
    return zonedDayStart(year, month, day, zone)
}

/** The day a picker selected, as the last instant of that day in `zone`. */
export const pickedDayEnd = (date: Date, zone: string = OPERATIONS_ZONE): Date => {
    const { year, month, day } = calendarPartsOf(date)
    return zonedDayEnd(year, month, day, zone)
}

/**
 * The short zone abbreviation at an instant, e.g. `EDT`, `PST`.
 *
 * Returns null for an unknown IANA id rather than throwing, so one bad value
 * cannot take out a table cell.
 */
export const zoneAbbreviation = (instant: Date, zone: string): string | null => {
    try {
        const parts = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'short' }).formatToParts(instant)
        return parts.find((part) => part.type === 'timeZoneName')?.value ?? null
    } catch {
        return null
    }
}

/**
 * The inverse of {@link pickedDayStart} / {@link pickedDayEnd}.
 *
 * A picker holds a `Date` whose only meaningful part is its **local** calendar day,
 * but what we store is an instant on an **Eastern** day. Rehydrating that instant
 * with a bare `new Date(iso)` reads its local calendar day, which is a different
 * day either side of the zone: in Los Angeles, Eastern midnight on 3 Sep is still
 * 2 Sep locally, so reopening the drawer and re-emitting walked the window back a
 * day each time; east of Eastern the "To" bound walked forward instead.
 *
 * So the instant is converted back through the Eastern calendar day it belongs to,
 * and a local `Date` is built for that day — making the round trip stable in any
 * browser zone.
 */
export const pickerDateFromZonedIso = (iso: string | null | undefined, zone: string = OPERATIONS_ZONE): Date | null => {
    if (!iso) return null
    const instant = new Date(iso)
    if (Number.isNaN(instant.getTime())) return null

    try {
        const parts = new Intl.DateTimeFormat('en-US', {
            timeZone: zone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        }).formatToParts(instant)

        const field = (type: Intl.DateTimeFormatPartTypes): number => Number(parts.find((part) => part.type === type)?.value ?? '0')

        return new Date(field('year'), field('month') - 1, field('day'))
    } catch {
        return null
    }
}
